import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col, message, Typography, Space, Alert } from 'antd';
import { QuestionCircleOutlined, CheckCircleOutlined, CopyOutlined, CloseCircleOutlined } from '@ant-design/icons';
import PartnerEnquiryService, { PartnerEnquiryFormValues } from '../../services/PartnerEnquiryService';

const { Text, Paragraph } = Typography;

interface PartnerEnquiryFormProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit?: (values: PartnerEnquiryFormValues) => void;
}

const PartnerEnquiryForm: React.FC<PartnerEnquiryFormProps> = ({
  visible,
  onCancel,
  onSubmit
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [enquiryNumber, setEnquiryNumber] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSubmit = async (values: PartnerEnquiryFormValues) => {
    if (onSubmit) {
      onSubmit(values);
      form.resetFields();
      onCancel();
      return;
    }

    // Clear any previous errors
    setErrorMessage('');

    // Default behavior: submit to API
    setSubmitting(true);
    try {
      const response = await PartnerEnquiryService.submitPartnerEnquiry(values);
      console.log('Full API response:', JSON.stringify(response, null, 2));
      
      if (response.status === 'success') {
        // Show success modal with enquiry number
        // Backend uses SNAKE_CASE, so response.data will have enquiry_number
        const enquiryData = response.data as any;
        console.log('📦 Full API response:', JSON.stringify(response, null, 2));
        console.log('📋 Enquiry data received:', enquiryData);
        console.log('🔑 Enquiry data keys:', enquiryData ? Object.keys(enquiryData) : 'null/undefined');
        
        // Extract enquiry number - backend returns snake_case (enquiry_number)
        let enquiryNum = '';
        if (enquiryData) {
          // Try all possible field name variations
          enquiryNum = enquiryData.enquiry_number 
            || enquiryData.enquiryNumber 
            || enquiryData['enquiry_number']
            || enquiryData['enquiryNumber']
            || '';
          
          // Log all possible fields for debugging
          console.log('🔍 enquiry_number (snake_case):', enquiryData.enquiry_number);
          console.log('🔍 enquiryNumber (camelCase):', enquiryData.enquiryNumber);
          console.log('📊 All enquiryData properties:', Object.entries(enquiryData));
          
          // If still not found, try to find any field containing "enquiry" and "number"
          if (!enquiryNum && enquiryData) {
            const keys = Object.keys(enquiryData);
            const enquiryKey = keys.find(key => 
              key.toLowerCase().includes('enquiry') && 
              key.toLowerCase().includes('number')
            );
            if (enquiryKey) {
              enquiryNum = enquiryData[enquiryKey] || '';
              console.log('✅ Found enquiry number using key:', enquiryKey, '=', enquiryNum);
            }
          }
        }
        
        console.log('✅ Final enquiry number extracted:', enquiryNum);
        
        if (!enquiryNum) {
          console.error('❌ Enquiry number not found!');
          console.error('📄 Full response.data:', JSON.stringify(response.data, null, 2));
          console.error('📄 Full response:', JSON.stringify(response, null, 2));
        }
        
        setEnquiryNumber(enquiryNum || '');
        setSuccessModalVisible(true);
        form.resetFields();
      } else {
        // Show error in the form
        setErrorMessage(response.message || 'Failed to submit enquiry. Please try again.');
      }
    } catch (error: any) {
      console.error('Error submitting partner enquiry:', error);
      // Handle validation errors from backend
      // The error response structure: { status: 'error', message: '...', error: '...' }
      let errorMsg = 'Failed to submit enquiry. Please try again.';
      
      if (error?.response?.data) {
        // Check if it's an ApiResponse format
        errorMsg = error.response.data.message || error.response.data.error || errorMsg;
      } else if (error?.message) {
        errorMsg = error.message;
      }
      
      // Show error in the form instead of toast
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSuccessModalVisible(false);
    setEnquiryNumber('');
    setErrorMessage('');
    onCancel();
  };

  const handleSuccessModalClose = () => {
    setSuccessModalVisible(false);
    setEnquiryNumber('');
    onCancel();
  };

  const copyEnquiryNumber = () => {
    if (enquiryNumber) {
      navigator.clipboard.writeText(enquiryNumber);
      message.success('Enquiry number copied to clipboard!');
    }
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <QuestionCircleOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
            <span>Partner Enquiry Form</span>
          </div>
        }
        open={visible && !successModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        centered
      >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '24px' }}
      >
        {/* Error Alert */}
        {errorMessage && (
          <Alert
            message={
              errorMessage.includes('Reference Enquiry Number:') ? (() => {
                const parts = errorMessage.split('Reference Enquiry Number:');
                const beforeNumber = parts[0];
                const afterNumber = parts[1]?.trim() || '';
                // Extract the enquiry number (12 alphanumeric characters)
                const enquiryNumberMatch = afterNumber.match(/^([A-Z0-9]{12})/);
                const enquiryNumber = enquiryNumberMatch ? enquiryNumberMatch[1] : afterNumber.split(' ')[0];
                const restOfMessage = enquiryNumberMatch ? afterNumber.substring(enquiryNumber.length).trim() : '';
                
                return (
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      {beforeNumber}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: '#fff7e6',
                      borderRadius: '4px',
                      border: '1px solid #ffd591'
                    }}>
                      <Text strong style={{ color: '#666', fontSize: '14px' }}>
                        Reference Enquiry Number:
                      </Text>
                      <Text 
                        strong 
                        copyable={{ text: enquiryNumber, tooltips: ['Copy', 'Copied!'] }}
                        style={{ 
                          fontSize: '18px', 
                          color: '#1890ff',
                          fontFamily: 'monospace',
                          letterSpacing: '2px',
                          backgroundColor: '#f0f9ff',
                          padding: '4px 12px',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          border: '1px solid #91d5ff'
                        }}
                      >
                        {enquiryNumber}
                      </Text>
                    </div>
                    {restOfMessage && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                        {restOfMessage}
                      </div>
                    )}
                  </div>
                );
              })() : (
                <div>{errorMessage}</div>
              )
            }
            type="error"
            icon={<CloseCircleOutlined />}
            closable
            onClose={() => setErrorMessage('')}
            showIcon
            style={{ marginBottom: '20px' }}
          />
        )}

        <Form.Item
          name="organizationName"
          label="Organization/Company Name"
          rules={[{ required: true, message: 'Please enter your organization name' }]}
        >
          <Input placeholder="Enter your organization name" size="large" />
        </Form.Item>

        <Form.Item
          name="contactPerson"
          label="Contact Person Name"
          rules={[{ required: true, message: 'Please enter contact person name' }]}
        >
          <Input placeholder="Enter contact person name" size="large" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input placeholder="your.email@example.com" size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label="Phone Number"
              rules={[{ required: true, message: 'Please enter your phone number' }]}
            >
              <Input placeholder="+91 1234567890" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="location"
              label="Location/City"
              rules={[{ required: true, message: 'Please enter your location' }]}
            >
              <Input placeholder="e.g., Kolkata, Mumbai" size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="partnershipType"
              label="Type of Partnership"
              rules={[{ required: true, message: 'Please select partnership type' }]}
            >
              <Select placeholder="Select partnership type" size="large">
                <Select.Option value="Consultancy">Consultancy</Select.Option>
                <Select.Option value="Educational Institution">Educational Institution</Select.Option>
                <Select.Option value="Corporate Partner">Corporate Partner</Select.Option>
                <Select.Option value="Regional Partner">Regional Partner</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="message"
          label="Message"
          rules={[{ required: true, message: 'Please enter your message' }]}
        >
          <Input.TextArea 
            rows={5} 
            placeholder="Tell us about your organization and how you'd like to partner with enpropeL..."
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Button 
                type="primary" 
                htmlType="submit" 
                block 
                size="large"
                loading={submitting}
                disabled={submitting}
              >
                Submit Enquiry
              </Button>
            </Col>
            <Col span={12}>
              <Button 
                onClick={handleCancel} 
                block 
                size="large"
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>

    {/* Success Modal */}
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Enquiry Submitted Successfully!</span>
        </div>
      }
      open={successModalVisible}
      onCancel={handleSuccessModalClose}
      footer={[
        <Button key="close" type="primary" onClick={handleSuccessModalClose} block size="large">
          Close
        </Button>
      ]}
      width={500}
      centered
      closable={true}
    >
      <div style={{ padding: '20px 0', textAlign: 'center' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Paragraph style={{ fontSize: '16px', marginBottom: '8px' }}>
            Thank you for your interest in partnering with enpropeL! We have received your enquiry and will get back to you within 48 to 72 hours.
          </Paragraph>
          
          <div style={{ 
            background: '#f0f9ff', 
            border: '2px solid #1890ff', 
            borderRadius: '8px', 
            padding: '16px',
            margin: '16px 0'
          }}>
            <Text strong style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '8px' }}>
              Your Enquiry Number:
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              {enquiryNumber ? (
                <>
                  <Text 
                    style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      color: '#1890ff',
                      letterSpacing: '2px',
                      fontFamily: 'monospace'
                    }}
                  >
                    {enquiryNumber}
                  </Text>
                  <Button 
                    type="text" 
                    icon={<CopyOutlined />} 
                    onClick={copyEnquiryNumber}
                    size="small"
                    title="Copy enquiry number"
                  />
                </>
              ) : (
                <Text style={{ color: '#ff4d4f', fontSize: '14px' }}>
                  Enquiry number not available. Please contact support.
                </Text>
              )}
            </div>
          </div>

          <Paragraph style={{ fontSize: '14px', color: '#666', marginTop: '16px' }}>
            Please save this enquiry number for future reference. You can use it to track the status of your enquiry.
          </Paragraph>
        </Space>
      </div>
    </Modal>
    </>
  );
};

export default PartnerEnquiryForm;

